import React from 'react';
import { Col } from 'react-bootstrap';
import Button from '../../../../../components/button';
import { ShiftChangeRequestColleagueApprovalStatus } from '../../../__generated__/graphql';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import ConfirmModal from '../../../../../components/Modal/ConfirmModal';

type ApproveRejectActionsCellProps = {
  requestId: string;
  requestToRespond: {
    id: string;
    status: ShiftChangeRequestColleagueApprovalStatus;
  } | null;
  isResponding: boolean;
  onAcceptClick: (requestId: string) => void;
  onDeclineClick: (requestId: string) => void;
};

export const ApproveRejectActionsCell: React.FC<
  ApproveRejectActionsCellProps
> = ({
  requestId,
  requestToRespond,
  isResponding,
  onAcceptClick,
  onDeclineClick,
}) => {
  const t = useNamespacedTranslation('schedulerShifts.shiftChangeRequest');

  const isThisRequest = requestToRespond?.id === requestId;
  const isInProgress = isResponding && isThisRequest;

  return (
    <Col className="col-auto">
      <Button
        label={t('accept')}
        isProcessing={
          isInProgress &&
          requestToRespond?.status ===
            ShiftChangeRequestColleagueApprovalStatus.Accepted
        }
        disabled={isResponding}
        variant="outline-primary"
        size="sm"
        icon="checkmark-outline"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onAcceptClick(requestId);
        }}
      />
      <Button
        label={t('decline')}
        isProcessing={
          isInProgress &&
          requestToRespond?.status ===
            ShiftChangeRequestColleagueApprovalStatus.Declined
        }
        disabled={isResponding}
        icon="close-outline"
        variant="outline-danger"
        className="ms-2"
        size="sm"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDeclineClick(requestId);
        }}
      />
    </Col>
  );
};

type ApproveRejectModalProps = {
  requestToRespond: {
    id: string;
    status: ShiftChangeRequestColleagueApprovalStatus;
  } | null;
  isResponding: boolean;
  onConfirm: () => Promise<void>;
  onClose: () => void;
};

export const ApproveRejectModal: React.FC<ApproveRejectModalProps> = ({
  requestToRespond,
  isResponding,
  onConfirm,
  onClose,
}) => {
  const t = useNamespacedTranslation('schedulerShifts.shiftChangeRequest');

  if (!requestToRespond) {
    return null;
  }

  return (
    <ConfirmModal
      show={!!requestToRespond}
      loading={isResponding}
      title={
        requestToRespond.status ===
        ShiftChangeRequestColleagueApprovalStatus.Accepted
          ? t('respondConfirmation.acceptTitle')
          : t('respondConfirmation.declineTitle')
      }
      text={
        requestToRespond.status ===
        ShiftChangeRequestColleagueApprovalStatus.Accepted
          ? t('respondConfirmation.acceptMessage')
          : t('respondConfirmation.declineMessage')
      }
      onConfirm={onConfirm}
      onClose={onClose}
      yesLabel={
        requestToRespond.status ===
        ShiftChangeRequestColleagueApprovalStatus.Accepted
          ? t('accept')
          : t('decline')
      }
      noLabel={t('cancel')}
    />
  );
};
